import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  TableSortLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as AnalyzeIcon,
  TrendingUp as MomentumIcon,
  ShowChart as MeanRevIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  OpenInNew as OpenInNewIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { stockService } from '../../services/stockService';

const getScreenerSymbol = (symbol: string): string => {
  return symbol.replace('.NS', '').replace('.BO', '');
};

interface SwingSignal {
  id: string;
  stock_id: string;
  symbol: string;
  stock_name: string;
  signal_date: string;
  signal_type: string;
  entry_price: number;
  stop_loss: number;
  target_price: number;
  target_price_2: number;
  risk_reward_ratio: number;
  risk_pct: number;
  reward_pct: number;
  momentum_score: number;
  mean_reversion_score: number;
  combined_score: number;
  win_probability: number;
  sharpe_ratio_30d: number;
  max_drawdown_30d: number;
  volatility_30d: number;
  rsi: number;
  support_level: number;
  resistance_level: number;
  volume_ratio: number;
  vcp_score: number;
  status: string;
  signal_reasons: any;
  expiry_date: string;
}

interface Performance {
  total_completed: number;
  target_hit: number;
  stopped_out: number;
  win_rate: number;
  avg_return: number;
  best_return: number;
  worst_return: number;
  momentum_signals: number;
  momentum_win_rate: number;
  mean_rev_signals: number;
  mean_rev_win_rate: number;
}

type SignalType = 'all' | 'momentum_breakout' | 'mean_reversion';
type SortField = 'combined_score' | 'risk_reward_ratio' | 'win_probability' | 'signal_date';

const SwingTradesTab: React.FC = () => {
  const navigate = useNavigate();
  const [signals, setSignals] = useState<SwingSignal[]>([]);
  const [performance, setPerformance] = useState<Performance | null>(null);
  const [signalType, setSignalType] = useState<SignalType>('all');
  const [status, setStatus] = useState<string>('active');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  
  // Favorites filter
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [watchlistCount, setWatchlistCount] = useState(0);
  
  // Sorting
  const [sortField, setSortField] = useState<SortField>('combined_score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Expanded row
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  
  // Status update dialog
  const [statusDialog, setStatusDialog] = useState<{open: boolean; signal: SwingSignal | null; newStatus: string}>({
    open: false,
    signal: null,
    newStatus: ''
  });
  const [exitPrice, setExitPrice] = useState<string>('');

  useEffect(() => {
    loadSignals();
  }, [signalType, status, sortField, sortOrder, favoritesOnly]);

  const loadSignals = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (favoritesOnly) {
        response = await stockService.getSwingSignalsFavorites({
          signal_type: signalType === 'all' ? undefined : signalType,
          status: status,
          min_score: 0,
          sort_by: sortField,
        });
        setWatchlistCount(response.watchlist_count || 0);
      } else {
        response = await stockService.getSwingSignals({
          signal_type: signalType === 'all' ? undefined : signalType,
          status: status,
          min_score: 0,
          limit: 200,
          sort_by: sortField,
        });
      }
      
      setSignals(response.signals || []);
      if (response.performance) {
        setPerformance(response.performance);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load swing signals');
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerAnalysis = async () => {
    try {
      setAnalyzing(true);
      
      if (favoritesOnly) {
        await stockService.analyzeSwingSignalsFavorites();
      } else {
        await stockService.analyzeSwingSignals();
      }

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusData = await stockService.getStrategyAnalysisStatus();
          if (!statusData.swing_analysis_running) {
            clearInterval(pollInterval);
            setAnalyzing(false);
            loadSignals();
          }
        } catch (e) {
          clearInterval(pollInterval);
          setAnalyzing(false);
        }
      }, 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to start analysis');
      setAnalyzing(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!statusDialog.signal || !statusDialog.newStatus) return;
    
    try {
      const response = await fetch(`/api/strategies/swing-signals/${statusDialog.signal.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          status: statusDialog.newStatus,
          exit_price: exitPrice ? parseFloat(exitPrice) : undefined
        })
      });
      
      if (response.ok) {
        setStatusDialog({ open: false, signal: null, newStatus: '' });
        setExitPrice('');
        loadSignals();
      } else {
        setError('Failed to update signal status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 70) return '#4caf50';
    if (score >= 50) return '#ff9800';
    return '#f44336';
  };

  const getSignalTypeIcon = (type: string) => {
    return type === 'momentum_breakout' ? (
      <MomentumIcon sx={{ fontSize: 16, mr: 0.5 }} />
    ) : (
      <MeanRevIcon sx={{ fontSize: 16, mr: 0.5 }} />
    );
  };

  const getStatusColor = (status: string): "default" | "primary" | "success" | "error" | "warning" => {
    switch (status) {
      case 'active': return 'primary';
      case 'triggered': return 'warning';
      case 'target_hit': return 'success';
      case 'stopped_out': return 'error';
      case 'expired': return 'default';
      default: return 'default';
    }
  };

  const formatSignalType = (type: string): string => {
    return type === 'momentum_breakout' ? 'Momentum' : 'Mean Reversion';
  };

  return (
    <Box>
      {/* Performance Summary */}
      {performance && performance.total_completed > 0 && (
        <Card sx={{ mb: 1, bgcolor: 'grey.50' }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Typography variant="subtitle2" gutterBottom>Historical Performance</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Win Rate</Typography>
                <Typography variant="h6" color={performance.win_rate >= 50 ? 'success.main' : 'error.main'}>
                  {performance.win_rate?.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Avg Return</Typography>
                <Typography variant="h6" color={performance.avg_return >= 0 ? 'success.main' : 'error.main'}>
                  {performance.avg_return?.toFixed(1)}%
                </Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Completed</Typography>
                <Typography variant="h6">{performance.total_completed}</Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Target Hit</Typography>
                <Typography variant="h6" color="success.main">{performance.target_hit}</Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Stopped Out</Typography>
                <Typography variant="h6" color="error.main">{performance.stopped_out}</Typography>
              </Grid>
              <Grid item xs={6} sm={2}>
                <Typography variant="caption" color="text.secondary">Best/Worst</Typography>
                <Typography variant="body2">
                  <span style={{ color: '#4caf50' }}>+{performance.best_return?.toFixed(1)}%</span> / 
                  <span style={{ color: '#f44336' }}>{performance.worst_return?.toFixed(1)}%</span>
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Filters and Controls */}
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} md={4}>
              <ToggleButtonGroup
                value={signalType}
                exclusive
                onChange={(_, value) => value && setSignalType(value)}
                size="small"
                fullWidth
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="momentum_breakout">
                  <MomentumIcon sx={{ mr: 0.5 }} /> Momentum
                </ToggleButton>
                <ToggleButton value="mean_reversion">
                  <MeanRevIcon sx={{ mr: 0.5 }} /> Mean Rev
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="triggered">Triggered</MenuItem>
                  <MenuItem value="target_hit">Target Hit</MenuItem>
                  <MenuItem value="stopped_out">Stopped Out</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={favoritesOnly}
                    onChange={(e) => setFavoritesOnly(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {favoritesOnly ? (
                      <StarIcon sx={{ fontSize: 16, color: 'gold' }} />
                    ) : (
                      <StarBorderIcon sx={{ fontSize: 16, color: 'grey.500' }} />
                    )}
                    <span>Favorites</span>
                    {favoritesOnly && watchlistCount > 0 && (
                      <Chip label={watchlistCount} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                }
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={analyzing ? <CircularProgress size={16} /> : <AnalyzeIcon />}
                  onClick={handleTriggerAnalysis}
                  disabled={analyzing}
                  size="small"
                >
                  {analyzing ? 'Scanning...' : favoritesOnly ? 'Scan Favorites' : 'Scan for Signals'}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadSignals}
                  disabled={loading}
                  size="small"
                >
                  Refresh
                </Button>
                
                <IconButton
                  size="small"
                  onClick={() => setHelpOpen(true)}
                  title="How signals are calculated"
                  sx={{ ml: 0.5 }}
                >
                  <HelpIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Signals Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : signals.length > 0 ? (
        <TableContainer component={Paper} sx={{ '& .MuiTableCell-root': { py: 0.25, px: 0.75, fontSize: '0.8rem' } }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }}></TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Entry</TableCell>
                <TableCell align="right">Stop Loss</TableCell>
                <TableCell align="right">Target</TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'risk_reward_ratio'}
                    direction={sortField === 'risk_reward_ratio' ? sortOrder : 'desc'}
                    onClick={() => handleSort('risk_reward_ratio')}
                  >
                    R:R
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'win_probability'}
                    direction={sortField === 'win_probability' ? sortOrder : 'desc'}
                    onClick={() => handleSort('win_probability')}
                  >
                    Win Prob
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortField === 'combined_score'}
                    direction={sortField === 'combined_score' ? sortOrder : 'desc'}
                    onClick={() => handleSort('combined_score')}
                  >
                    Score
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signals.map((signal) => (
                <React.Fragment key={signal.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton 
                        size="small"
                        onClick={() => setExpandedRow(expandedRow === signal.id ? null : signal.id)}
                      >
                        {expandedRow === signal.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                          onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(signal.symbol)}`)}
                        >
                          {signal.symbol}
                        </Typography>
                        <a
                          href={`https://www.screener.in/company/${getScreenerSymbol(signal.symbol)}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ display: 'flex', alignItems: 'center' }}
                          title="View on Screener.in"
                        >
                          <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary', '&:hover': { color: 'primary.main' } }} />
                        </a>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {signal.signal_date}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getSignalTypeIcon(signal.signal_type)}
                        label={formatSignalType(signal.signal_type)}
                        size="small"
                        variant="outlined"
                        color={signal.signal_type === 'momentum_breakout' ? 'primary' : 'secondary'}
                      />
                    </TableCell>
                    <TableCell align="right">₹{signal.entry_price?.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>
                      ₹{signal.stop_loss?.toFixed(2)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        -{signal.risk_pct?.toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>
                      ₹{signal.target_price?.toFixed(2)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        +{signal.reward_pct?.toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${signal.risk_reward_ratio?.toFixed(1)}:1`}
                        size="small"
                        sx={{
                          bgcolor: signal.risk_reward_ratio >= 2 ? '#4caf50' : signal.risk_reward_ratio >= 1.5 ? '#ff9800' : '#f44336',
                          color: 'white'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {signal.win_probability?.toFixed(0)}%
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={signal.combined_score?.toFixed(0)}
                        size="small"
                        sx={{
                          bgcolor: getScoreColor(signal.combined_score),
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={signal.status?.replace('_', ' ').toUpperCase()}
                        size="small"
                        color={getStatusColor(signal.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {signal.status === 'active' && (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Mark Triggered">
                            <IconButton 
                              size="small" 
                              onClick={() => setStatusDialog({ open: true, signal, newStatus: 'triggered' })}
                            >
                              <MoneyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                      {signal.status === 'triggered' && (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title="Target Hit">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => setStatusDialog({ open: true, signal, newStatus: 'target_hit' })}
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Stopped Out">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => setStatusDialog({ open: true, signal, newStatus: 'stopped_out' })}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell colSpan={11} sx={{ py: 0 }}>
                      <Collapse in={expandedRow === signal.id} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1, px: 1.5 }}>
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                              <Typography variant="subtitle2" gutterBottom>Signal Reasons</Typography>
                              {signal.signal_reasons && Object.entries(signal.signal_reasons).map(([key, value]) => (
                                <Typography key={key} variant="caption" display="block">
                                  <strong>{key.replace(/_/g, ' ')}:</strong> {String(value)}
                                </Typography>
                              ))}
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="subtitle2" gutterBottom>Technical Indicators</Typography>
                              <Typography variant="caption" display="block">
                                RSI: {signal.rsi?.toFixed(1) || '-'}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Support: ₹{signal.support_level?.toFixed(2) || '-'}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Resistance: ₹{signal.resistance_level?.toFixed(2) || '-'}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Volume Ratio: {signal.volume_ratio?.toFixed(1) || '-'}x
                              </Typography>
                              {signal.vcp_score && (
                                <Typography variant="caption" display="block">
                                  VCP Score: {signal.vcp_score?.toFixed(0)}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="subtitle2" gutterBottom>Risk Metrics</Typography>
                              <Typography variant="caption" display="block">
                                Sharpe Ratio (30d): {signal.sharpe_ratio_30d?.toFixed(2) || '-'}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Max Drawdown (30d): {signal.max_drawdown_30d?.toFixed(1) || '-'}%
                              </Typography>
                              <Typography variant="caption" display="block">
                                Volatility (30d): {signal.volatility_30d?.toFixed(1) || '-'}%
                              </Typography>
                              {signal.target_price_2 && (
                                <Typography variant="caption" display="block">
                                  Target 2: ₹{signal.target_price_2?.toFixed(2)}
                                </Typography>
                              )}
                              <Typography variant="caption" display="block" color="text.secondary">
                                Expires: {signal.expiry_date}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info">
          No swing signals found. Try running a new analysis or changing filters.
        </Alert>
      )}
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Showing {signals.length} signals. Momentum: VCP breakouts with volume surge. Mean Reversion: Oversold at support.
      </Typography>

      {/* Help Dialog */}
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Swing Trade Signals — How It Works</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
            Momentum Breakout
          </Typography>
          <Typography variant="body2" paragraph>
            Identifies stocks with a <strong>VCP (Volatility Contraction Pattern)</strong> breakout.
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>VCP Score &gt; 50</strong> — pattern must show clear contracting ranges</li>
              <li><strong>Volume surge ≥ 1.5×</strong> 20-day average — confirms institutional interest</li>
              <li><strong>Price near pivot</strong> — within 3% of the breakout level</li>
              <li><strong>Score</strong> = 40% VCP quality + 30% volume ratio + 30% relative strength</li>
              <li><strong>Win probability</strong> = from VCP breakout probability (default 60%)</li>
              <li><strong>Stop loss</strong> = 8% below entry; <strong>Target</strong> = 20% above entry</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Mean Reversion
          </Typography>
          <Typography variant="body2" paragraph>
            Identifies <strong>oversold stocks bouncing off support</strong>.
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>RSI &lt; 35</strong> — stock is oversold</li>
              <li><strong>Price within 5% of support</strong> — near a historically tested level</li>
              <li><strong>No death cross</strong> in last 90 days — avoids downtrends</li>
              <li><strong>Score</strong> = RSI depth × 2 + support proximity × 10 + risk:reward bonus</li>
              <li><strong>Win probability</strong> = 50% + 5% per support touch (max 70%)</li>
              <li><strong>Stop loss</strong> = below support; <strong>Target</strong> = resistance level or 10% gain</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Risk Metrics (in expanded row)
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>R:R (Risk:Reward)</strong> — ratio of potential gain to potential loss. ≥ 2:1 is good (green)</li>
              <li><strong>Sharpe Ratio</strong> — risk-adjusted returns over 30 days. &gt; 1.0 is good</li>
              <li><strong>Max Drawdown</strong> — largest peak-to-trough drop in 30 days</li>
              <li><strong>Volatility</strong> — standard deviation of 30-day returns</li>
            </ul>
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            Signal Lifecycle
          </Typography>
          <Typography variant="body2">
            Active → Triggered (entered trade) → Target Hit ✅ or Stopped Out ❌. Signals expire after 30 days.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog.open} onClose={() => setStatusDialog({ open: false, signal: null, newStatus: '' })}>
        <DialogTitle>
          Update Signal Status
        </DialogTitle>
        <DialogContent>
          {statusDialog.signal && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{statusDialog.signal.symbol}</strong> - {formatSignalType(statusDialog.signal.signal_type)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Entry: ₹{statusDialog.signal.entry_price?.toFixed(2)} | 
                Stop: ₹{statusDialog.signal.stop_loss?.toFixed(2)} | 
                Target: ₹{statusDialog.signal.target_price?.toFixed(2)}
              </Typography>
            </Box>
          )}
          
          {(statusDialog.newStatus === 'target_hit' || statusDialog.newStatus === 'stopped_out') && (
            <TextField
              label="Exit Price"
              type="number"
              fullWidth
              value={exitPrice}
              onChange={(e) => setExitPrice(e.target.value)}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>
              }}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, signal: null, newStatus: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateStatus} 
            variant="contained"
            color={statusDialog.newStatus === 'target_hit' ? 'success' : statusDialog.newStatus === 'stopped_out' ? 'error' : 'primary'}
          >
            Confirm {statusDialog.newStatus?.replace('_', ' ')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SwingTradesTab;
